// Écran d'accueil façon Game Boy (porté de wario/src/main.ts).
// HUB → JOUER / BOUTIQUE / CLASSEMENT / PROFIL / PERSONNALISER / LANGUE.
// JOUER → choix du style → choix du mode (possédés) → partie.
// Navigation : A = entrer/valider, B = retour, ▲▼ = naviguer. Les modes/styles/noms
// se débloquent en BOUTIQUE (Telegram Stars). Résout { theme, lang, mode }.
import { THEMES, type Theme } from "./game/themes";
import { UI, saveLang, LANGS, type Lang } from "./game/i18n";
import { MODES, loadMode, saveMode, type Mode } from "./game/modes";
import { customText, setCustom, MAX_LEN } from "./game/custom";
import { tg, haptic, shareLink, referralUrl } from "./telegram";
import {
  getMe,
  getLeaderboard,
  getCatalog,
  createInvoice,
  claimItem,
  simulatePurchase,
  buyWithCoins,
  buyBooster,
  getDaily,
  claimDailyObjective,
  getFeatured,
  resetMe,
  type CatalogItem,
  type BoosterDef,
  type DailyObjective,
  type FeaturedResponse,
} from "./api";
import {
  ownsItem,
  isAdmin,
  getCoins,
  setCoins,
  getUserId,
  getBoosterQty,
  setBoosters,
  refreshEntitlements,
} from "./entitlements";
import { sfx, isSoundEnabled, setSoundEnabled } from "./sound";

/** Échappe le HTML (noms de joueurs venant de Telegram). */
function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );
}

// Verrouillage par droit (modifiable). Sans le droit possédé : prix affiché + achat sur place.
const THEME_ITEM: Record<string, string> = { zombie: "theme_zombie" };
function themeLocked(t: Theme): boolean {
  const item = THEME_ITEM[t.key];
  return !!item && !ownsItem(item);
}
/** Mode possédé ? Classique gratuit, sinon via le pack ou l'achat à l'unité. */
function modeOwned(m: Mode): boolean {
  return m.key === "classic" || ownsItem("modes_pack") || ownsItem(`mode_${m.key}`);
}

const ENDONYM: Record<Lang, string> = {
  fr: "Français",
  en: "English",
  ru: "Русский",
  es: "Español",
  pt: "Português",
};

const SECTIONS = ["modes", "styles", "coins", "extras"] as const;

// Catalogue mis en cache (statique) : permet l'achat « sur place » des thèmes/modes
// (afficher le prix + ouvrir le paiement sans passer par la boutique).
let catalogItems: CatalogItem[] = [];
let catalogBoosters: BoosterDef[] = [];
async function ensureCatalog(): Promise<void> {
  if (catalogItems.length) return;
  const res = await getCatalog();
  if (res.ok) {
    catalogItems = res.data.items;
    catalogBoosters = res.data.boosters ?? [];
  }
}

// Icônes des boosters (libellés via i18n boostSlow/boostDouble/boostShield).
const BOOSTER_ICON: Record<string, string> = { slow: "🐌", double: "💰", shield: "🛡️" };
const itemById = (id: string): CatalogItem | null =>
  catalogItems.find((i) => i.id === id) ?? null;

// Combinaison du jour (mode+style, pièces ×N) — jouable même sans posséder le mode.
let featured: FeaturedResponse | null = null;
async function ensureFeatured(): Promise<void> {
  if (featured) return;
  const res = await getFeatured();
  if (res.ok) featured = res.data;
}

export function showMenu(
  root: HTMLElement,
  initialLang: Lang,
): Promise<{ theme: Theme; lang: Lang; mode: Mode; booster?: string }> {
  return new Promise((resolve) => {
    let lang = initialLang;
    let mode = loadMode();
    const themes = Object.values(THEMES);
    let chosenTheme: Theme | null = null; // style retenu, le temps de choisir le mode

    type View =
      | "hub"
      | "style"
      | "modepick"
      | "perso"
      | "lang"
      | "profile"
      | "board"
      | "shop"
      | "dev"
      | "daily";
    let view: View = "hub";
    let sel = 0;
    let rows: HTMLLIElement[] = [];
    let shopItems: CatalogItem[] = []; // articles sélectionnables (alignés sur rows) en boutique
    let customItem: CatalogItem | null = null; // article custom_names (prix), chargé à la demande
    let boardPeriod: "day" | "week" | "all" = "all"; // fenêtre du classement (A pour cycler)
    let dailyObjs: DailyObjective[] = []; // objectifs du jour (alignés sur rows)
    let claimableCount = 0; // récompenses à réclamer (badge 🎁 sur DÉFIS)
    let chosenBooster: string | null = null; // booster activé pour la partie à lancer
    const boosterLabel = (id: string): string => {
      const ui = UI[lang];
      return id === "slow" ? ui.boostSlow : id === "double" ? ui.boostDouble : ui.boostShield;
    };
    // Boosters possédés (pour la ligne cyclable du choix de mode).
    const ownedBoosters = (): string[] =>
      catalogBoosters.filter((b) => getBoosterQty(b.id) > 0).map((b) => b.id);
    // SANS → 🐌 → 💰 → 🛡️ → SANS… (uniquement les possédés).
    function cycleBooster(): void {
      const opts: (string | null)[] = [null, ...ownedBoosters()];
      const i = opts.indexOf(chosenBooster);
      chosenBooster = opts[(i + 1) % opts.length] ?? null;
      paint(); // re-rend le choix de mode (sélection conservée)
    }
    // Prix affiché : les DEUX monnaies quand l'article accepte pièces ET Stars
    // (évite le piège « prix en 🪙 mais facturé en ⭐ » quand le solde manque).
    const priceLabel = (it: CatalogItem) =>
      it.coins && it.stars
        ? `${it.coins}🪙·${it.stars}⭐`
        : it.coins
          ? `${it.coins}🪙`
          : it.stars === 0
            ? UI[lang].free
            : `${it.stars}⭐`;

    const gb = document.createElement("div");
    gb.className = "gb";
    gb.innerHTML = `
      <div class="gb-top"><span class="gb-led"></span>BATTERY · THE BIG DIG</div>
      <div class="gb-frame">
        <div class="gb-lcd">
          <div class="gb-title">THE<br />BIG DIG</div>
          <div class="gb-tag"></div>
          <ul class="gb-menu"></ul>
          <div class="gb-hint"></div>
        </div>
        <div class="gb-matrix">DOT MATRIX WITH STEREO SOUND</div>
      </div>
      <div class="gb-wordmark">The Big Dig<small>®</small></div>
      <div class="gb-controls">
        <div class="dpad">
          <button class="up" aria-label="Haut"></button>
          <button class="mid" tabindex="-1" aria-hidden="true"></button>
          <button class="down" aria-label="Bas"></button>
        </div>
        <div class="ab">
          <button class="btnB" aria-label="B — retour">B</button>
          <button class="btnA" aria-label="A — valider">A</button>
        </div>
      </div>
      <div class="gb-meta"><button class="btnSelect">SELECT</button><span>START</span></div>`;

    const menu = gb.querySelector(".gb-menu")!;
    const tagEl = gb.querySelector(".gb-tag")!;
    const hintEl = gb.querySelector(".gb-hint")!;

    // Message bref dans la ligne d'aide : erreur (⚠ rouge) ou confirmation (verte).
    let flashTimer = 0;
    let hintBefore: string | null = null;
    function flashHint(text: string, ok = false): void {
      if (hintBefore === null) hintBefore = hintEl.innerHTML;
      hintEl.innerHTML = ok
        ? `<span class="gb-flash-ok">✓ ${escapeHtml(text)}</span>`
        : `<span class="gb-flash">⚠ ${escapeHtml(text)}</span>`;
      clearTimeout(flashTimer);
      flashTimer = window.setTimeout(() => {
        if (hintBefore !== null) hintEl.innerHTML = hintBefore;
        hintBefore = null;
      }, 1600);
    }

    function applySel(): void {
      rows.forEach((r, j) => {
        const on = j === sel;
        r.classList.toggle("sel", on);
        if (on) r.scrollIntoView({ block: "nearest" }); // garde la sélection visible
      });
    }
    function rowHtml(label: string, value?: string): string {
      return (
        `<span class="cur">▶</span><span>${label}</span>` +
        (value != null ? `<span class="gb-lang">${value}</span>` : "")
      );
    }
    function addRow(html: string, isLang = false): void {
      const li = document.createElement("li");
      li.className = "gb-row" + (isLang ? " gb-row-lang" : "");
      li.innerHTML = html;
      const idx = rows.length;
      li.addEventListener("mouseenter", () => {
        sel = idx;
        applySel();
      });
      li.addEventListener("click", () => {
        sel = idx;
        onRowClick();
      });
      menu.appendChild(li);
      rows.push(li);
    }
    /** En-tête de section (non sélectionnable : pas ajouté à `rows`). */
    function addHeader(label: string): void {
      const li = document.createElement("li");
      li.className = "gb-section";
      li.textContent = label;
      menu.appendChild(li);
    }
    /** Ligne de saisie (PERSONNALISER) : vrai <input> → clavier natif au focus. */
    function addInputRow(field: "nose" | "hand", label: string): void {
      const li = document.createElement("li");
      li.className = "gb-row gb-row-lang";
      const cur = document.createElement("span");
      cur.className = "cur";
      cur.textContent = "▶";
      const lab = document.createElement("span");
      lab.textContent = label;
      const inp = document.createElement("input");
      inp.className = "gb-input";
      inp.maxLength = MAX_LEN;
      inp.value = customText[field];
      inp.autocapitalize = "characters";
      inp.autocomplete = "off";
      inp.spellcheck = false;
      inp.placeholder = "—";
      const idx = rows.length;
      inp.addEventListener("input", () => setCustom(field, inp.value));
      inp.addEventListener("focus", () => {
        sel = idx;
        applySel();
      });
      li.addEventListener("mouseenter", () => {
        sel = idx;
        applySel();
      });
      li.addEventListener("click", (e) => {
        if (e.target !== inp) inp.focus();
      });
      li.append(cur, lab, inp);
      menu.appendChild(li);
      rows.push(li);
    }

    function paint(): void {
      const ui = UI[lang];
      menu.innerHTML = "";
      rows = [];
      if (view === "hub") {
        // Solde de pièces toujours visible en tête (+ événement week-end).
        tagEl.textContent = `- ${getCoins()}🪙${featured?.weekend ? ` · 🎉×${featured.weekendMult}` : ""} -`;
        hintEl.innerHTML = "A ▸";
        // Combinaison du jour : jouable direct, pièces multipliées (essai gratuit).
        const fLabel = featured ? (ui.modeNames[featured.mode] ?? featured.mode) : null;
        addRow(
          rowHtml(`⭐ ${fLabel ? fLabel.toUpperCase() : "…"}`, featured ? `×${featured.mult}🪙` : ""),
          true,
        );
        addRow(rowHtml(ui.menuPlay));
        addRow(
          rowHtml(ui.menuDaily, claimableCount > 0 ? `🎁${claimableCount}` : undefined),
          claimableCount > 0,
        );
        addRow(rowHtml(ui.menuInvite, "+200🪙"), true);
        addRow(rowHtml(ui.menuShop));
        addRow(rowHtml(ui.menuBoard));
        addRow(rowHtml(ui.menuProfile));
        addRow(rowHtml(ui.menuCustom));
        addRow(rowHtml(ui.language, `▸ ${lang.toUpperCase()}`), true);
        addRow(rowHtml(ui.menuSound, isSoundEnabled() ? "♪ ON" : "♪ OFF"), true);
      } else if (view === "style") {
        tagEl.textContent = ui.pickTag;
        hintEl.innerHTML = "A:▶ &nbsp;·&nbsp; B:◀";
        themes.forEach((t) => {
          const locked = themeLocked(t);
          const it = locked ? itemById(THEME_ITEM[t.key]!) : null;
          const val = locked ? (it ? priceLabel(it) : "🔒") : undefined;
          addRow(rowHtml(t.label.toUpperCase(), val), locked);
        });
      } else if (view === "modepick") {
        tagEl.textContent = ui.pickMode;
        hintEl.innerHTML = "A:✓ &nbsp;·&nbsp; B:◀";
        MODES.forEach((m) => {
          const owned = modeOwned(m);
          const it = owned ? null : itemById(`mode_${m.key}`);
          const val = owned ? undefined : it ? priceLabel(it) : "🔒";
          addRow(rowHtml((ui.modeNames[m.key] ?? m.label).toUpperCase(), val), !owned);
        });
        // Ligne booster cyclable (si on en possède) : A dessus fait tourner le choix.
        if (ownedBoosters().length) {
          const val = chosenBooster
            ? `${BOOSTER_ICON[chosenBooster] ?? "★"} ${boosterLabel(chosenBooster)} ×${getBoosterQty(chosenBooster)}`
            : ui.boosterNone;
          addRow(rowHtml(`🎒 ${ui.secBoosters}`, val), true);
        }
      } else if (view === "perso") {
        tagEl.textContent = `- ${ui.menuCustom} -`;
        if (ownsItem("custom_names")) {
          hintEl.innerHTML = `${ui.typeName} &nbsp;·&nbsp; B:◀`;
          addInputRow("nose", ui.customNose);
          addInputRow("hand", ui.customHand);
        } else {
          // Déblocage sur place (jamais de saut vers la boutique).
          hintEl.innerHTML = "A:✓ &nbsp;·&nbsp; B:◀";
          const price = customItem ? (customItem.stars === 0 ? ui.free : `${customItem.stars}⭐`) : "…";
          addRow(rowHtml(ui.unlock, price), true);
        }
      } else if (view === "dev") {
        tagEl.textContent = "- DEV -";
        hintEl.innerHTML = "A:✓ &nbsp;·&nbsp; B:◀";
        addRow(rowHtml("RESET"));
      } else {
        // lang
        tagEl.textContent = `- ${ui.language} -`;
        hintEl.innerHTML = "A:✓ &nbsp;·&nbsp; B:◀";
        LANGS.forEach((l) => addRow(rowHtml(`${l.toUpperCase()}  ${ENDONYM[l]}`)));
      }
      applySel();
    }

    function move(d: number): void {
      const n = rows.length;
      if (n === 0) return; // vues de données : rien à parcourir
      sel = (sel + d + n) % n;
      applySel();
      sfx.nav();
    }
    function enter(v: View): void {
      view = v;
      if (v === "style") chosenBooster = null; // nouveau parcours → pas de booster présélectionné
      if (v === "profile" || v === "board") {
        sel = 0;
        rows = [];
        renderData(v);
        return;
      }
      if (v === "shop") {
        sel = 0;
        rows = [];
        renderShop();
        return;
      }
      if (v === "daily") {
        sel = 0;
        rows = [];
        renderDaily();
        return;
      }
      sel =
        v === "lang"
          ? Math.max(0, LANGS.indexOf(lang))
          : v === "modepick"
            ? Math.max(0, MODES.findIndex((m) => m.key === mode.key))
            : 0;
      paint();
    }

    // Affiche profil (/me) ou classement (/leaderboard) dans l'écran LCD. Chargement
    // asynchrone ; loadSeq évite qu'une réponse tardive écrase une vue déjà quittée.
    let loadSeq = 0;
    const dataRow = (label: string, value: string) =>
      `<li class="gb-row gb-row-lang gb-data"><span>${escapeHtml(label)}</span>` +
      `<span class="gb-lang">${escapeHtml(value)}</span></li>`;
    function renderData(v: "profile" | "board"): void {
      const ui = UI[lang];
      const seq = ++loadSeq;
      hintEl.innerHTML = "B:◀";
      menu.innerHTML = `<li class="gb-row gb-data">…</li>`;
      if (v === "profile") {
        tagEl.textContent = `- ${ui.menuProfile} -`;
        void getMe(tg?.initData ?? "").then((res) => {
          if (seq !== loadSeq) return;
          if (!res.ok) return void (menu.innerHTML = `<li class="gb-row gb-data">${escapeHtml(ui.netError)}</li>`);
          const s = res.data.stats;
          const lines = [
            dataRow("🪙", String(res.data.coins)),
            dataRow(ui.statTotal, String(s.total)),
            dataRow(ui.best, String(s.best)),
            dataRow(ui.statGames, String(s.games)),
            dataRow(ui.statRank, s.rank ? `#${s.rank}` : "—"),
            dataRow(`🤝 ${ui.menuInvite}`, String(res.data.referrals ?? 0)),
          ];
          for (const t of s.byTheme.slice(0, 3)) if (t.theme) lines.push(dataRow(t.theme.toUpperCase(), String(t.total)));
          menu.innerHTML = lines.join("");
        });
      } else {
        const pLabel = boardPeriod === "day" ? ui.periodDay : boardPeriod === "week" ? ui.periodWeek : ui.periodAll;
        tagEl.textContent = `- ${ui.menuBoard} · ${pLabel} -`;
        hintEl.innerHTML = "A:◷ &nbsp;·&nbsp; B:◀";
        void getLeaderboard({ limit: 15, period: boardPeriod }, tg?.initData).then((res) => {
          if (seq !== loadSeq) return;
          if (!res.ok) return void (menu.innerHTML = `<li class="gb-row gb-data">${escapeHtml(ui.netError)}</li>`);
          const { entries, me } = res.data;
          if (!entries.length)
            return void (menu.innerHTML = `<li class="gb-row gb-data">${escapeHtml(ui.boardEmpty)}</li>`);
          // Ligne classement (rang nom … total), surlignée si c'est moi.
          const boardRow = (rank: number, name: string, total: number, mine: boolean) =>
            `<li class="gb-row gb-row-lang gb-data${mine ? " gb-me" : ""}">` +
            `<span>${rank} ${escapeHtml(name)}</span><span class="gb-lang">${total}</span></li>`;
          let html = entries.map((e) => boardRow(e.rank, e.name, e.total, !!e.me)).join("");
          // Hors top : on ajoute ma position en bas (séparateur + ligne surlignée).
          if (me && !me.inTop) {
            html += `<li class="gb-section gb-sep">⋯</li>`;
            html += boardRow(me.rank, me.name, me.total, true);
          }
          menu.innerHTML = html;
        });
      }
    }

    /** Un article est-il déjà possédé ? (le pack couvre tous les modes). */
    function shopOwned(it: CatalogItem): boolean {
      if (it.coinsGrant) return false; // pack de pièces : rachetable à volonté
      if (ownsItem(it.id)) return true;
      return it.category === "modes" && it.id !== "modes_pack" && ownsItem("modes_pack");
    }
    // Boutique : catalogue groupé par sections (en-têtes non sélectionnables).
    function renderShop(): void {
      const ui = UI[lang];
      const seq = ++loadSeq;
      tagEl.textContent = `- ${ui.menuShop} · ${getCoins()}🪙 -`;
      hintEl.innerHTML = "A:✓ &nbsp;·&nbsp; B:◀";
      menu.innerHTML = `<li class="gb-row gb-data">…</li>`;
      rows = [];
      shopItems = [];
      void ensureCatalog().then(() => {
        if (seq !== loadSeq || view !== "shop") return;
        menu.innerHTML = "";
        rows = [];
        shopItems = [];
        if (!catalogItems.length) return void (menu.innerHTML = `<li class="gb-row gb-data">${escapeHtml(ui.netError)}</li>`);
        const labels: Record<string, string> = {
          modes: ui.secModes,
          styles: ui.secStyles,
          coins: ui.secCoins,
          extras: ui.secExtras,
        };
        for (const cat of SECTIONS) {
          const group = catalogItems.filter((it) => it.category === cat);
          if (!group.length) continue;
          addHeader(labels[cat] ?? cat.toUpperCase());
          for (const it of group) {
            shopItems.push(it);
            addRow(rowHtml(it.title.toUpperCase(), shopOwned(it) ? "✓" : priceLabel(it)), true);
          }
        }
        // Boosters consommables (achetés en pièces, rachetables à volonté).
        if (catalogBoosters.length) {
          addHeader(ui.secBoosters);
          for (const b of catalogBoosters) {
            const qty = getBoosterQty(b.id);
            addRow(
              rowHtml(
                `${BOOSTER_ICON[b.id] ?? "★"} ${boosterLabel(b.id)}${qty > 0 ? ` ×${qty}` : ""}`,
                `${b.coins}🪙`,
              ),
              true,
            );
          }
        }
        sel = 0;
        applySel();
      });
    }
    // Achat d'un booster (pièces) puis rafraîchit la boutique.
    async function buyBoosterItem(b: BoosterDef): Promise<void> {
      const r = await buyBooster(tg?.initData ?? "", b.id);
      if (!r.ok) {
        haptic("error");
        flashHint(UI[lang].noCoins);
        return;
      }
      haptic("success");
      setCoins(r.data.coins);
      setBoosters(r.data.boosters);
      if (view === "shop") renderShop();
    }
    // Objectifs du jour : progression + récompense réclamable (ligne surlignée).
    function renderDaily(): void {
      const ui = UI[lang];
      const seq = ++loadSeq;
      tagEl.textContent = `- ${ui.menuDaily} · ${getCoins()}🪙 -`;
      hintEl.innerHTML = "A:✓ &nbsp;·&nbsp; B:◀";
      menu.innerHTML = `<li class="gb-row gb-data">…</li>`;
      rows = [];
      dailyObjs = [];
      void getDaily(tg?.initData ?? "").then((res) => {
        if (seq !== loadSeq || view !== "daily") return;
        menu.innerHTML = "";
        rows = [];
        dailyObjs = [];
        if (!res.ok) return void (menu.innerHTML = `<li class="gb-row gb-data">${escapeHtml(ui.netError)}</li>`);
        const ICON: Record<string, string> = { games: "🎮", maxscore: "🏆", coins: "🪙" };
        // Les défis déjà réclamés disparaissent ; tout fait → message de fin.
        const remaining = res.data.objectives.filter((o) => !o.claimed);
        if (!remaining.length) {
          menu.innerHTML = `<li class="gb-row gb-data">✓ ${escapeHtml(ui.dailyDone)}</li>`;
          return;
        }
        remaining.forEach((o) => {
          dailyObjs.push(o);
          // Accompli → bouton explicite « 🎁 RÉCLAMER » ; sinon la récompense à venir.
          const value = o.done ? `🎁 ${ui.claim}` : `+${o.reward}🪙`;
          addRow(rowHtml(`${ICON[o.type] ?? "★"} ${o.progress}/${o.target}`, value), true);
          const li = rows[rows.length - 1]!;
          if (o.done) li.classList.add("gb-me", "gb-claim"); // réclamable (clignote)
          // Barre de progression sous la ligne.
          const pct = Math.round((Math.min(o.progress, o.target) / o.target) * 100);
          const bar = document.createElement("div");
          bar.className = "gb-bar";
          bar.innerHTML = `<div class="gb-bar-fill" style="width:${pct}%"></div>`;
          li.classList.add("gb-obj");
          li.appendChild(bar);
        });
        claimableCount = remaining.filter((o) => o.done).length;
        sel = 0;
        applySel();
      });
    }
    // Réclame l'objectif sélectionné (s'il est accompli et pas encore réclamé).
    async function claimObjective(o: DailyObjective): Promise<void> {
      if (!o.done || o.claimed) return;
      const r = await claimDailyObjective(tg?.initData ?? "", o.id);
      if (!r.ok) return void haptic("error");
      haptic("success");
      setCoins(r.data.coins);
      if (view === "daily") renderDaily();
    }

    // Obtient un article (gratuit → réclamation ; payant → facture Stars native) puis
    // exécute `onDone` une fois le droit accordé. Reste à l'écran courant.
    async function acquire(item: CatalogItem, onDone: () => void): Promise<void> {
      if (shopOwned(item)) return void onDone();
      const initData = tg?.initData ?? "";
      // Confirmation visible après un pack de pièces (« ✓ +500 🪙 »).
      const done = () => {
        onDone();
        if (item.coinsGrant) flashHint(`+${item.coinsGrant} 🪙`, true);
      };
      // Payable en pièces ET solde suffisant → achat en pièces (chemin gratuit).
      if (item.coins && getCoins() >= item.coins) {
        const r = await buyWithCoins(initData, item.id);
        if (!r.ok) {
          haptic("error");
          flashHint(UI[lang].noCoins);
          return;
        }
        haptic("success");
        await refreshEntitlements(initData);
        done();
        return;
      }
      // Gratuit → réclamation ; admin (dev) → simulation SANS vraies Stars.
      if (item.stars === 0 || isAdmin()) {
        const simulated = item.stars !== 0;
        const r = simulated
          ? await simulatePurchase(initData, item.id)
          : await claimItem(initData, item.id);
        if (!r.ok) return void haptic("error");
        haptic("success");
        await refreshEntitlements(initData);
        done();
        // Rappel visible : en admin, l'achat Stars est SIMULÉ (rien n'est débité).
        if (simulated)
          flashHint(item.coinsGrant ? `+${item.coinsGrant} 🪙 · ⭐ simulé` : "⭐ SIMULÉ (admin)", true);
        return;
      }
      const res = await createInvoice(tg?.initData ?? "", item.id);
      if (!res.ok || !tg?.openInvoice) return void haptic("error");
      tg.openInvoice(res.data.link, (status) => {
        if (status === "paid") {
          haptic("success");
          void refreshEntitlements(tg?.initData ?? "").then(done);
        } else if (status === "failed") {
          haptic("error");
        }
      });
    }
    // Achète un article par id (charge le catalogue au besoin) puis `onDone`.
    async function purchaseAndThen(itemId: string, onDone: () => void): Promise<void> {
      await ensureCatalog();
      const item = itemById(itemId);
      if (!item) return void haptic("error");
      await acquire(item, onDone);
    }
    function back(): void {
      if (view === "hub") return;
      sfx.back();
      if (view === "modepick") return enter("style"); // retour au choix du style
      enter("hub");
    }

    // PERSONNALISER : reste TOUJOURS sur cet écran. Si non débloqué, on charge le
    // prix et on propose le déblocage sur place ; sinon, les champs de saisie.
    function openPerso(): void {
      enter("perso");
      if (!ownsItem("custom_names")) {
        void ensureCatalog().then(() => {
          customItem = itemById("custom_names");
          if (view === "perso" && !ownsItem("custom_names")) paint();
        });
      }
    }

    // Menu DEV (SELECT) — visible/utilisable seulement par un admin.
    function openDev(): void {
      if (!isAdmin()) return;
      if (view === "dev") back();
      else enter("dev");
    }
    // RESET : efface mes droits + paiements (comme si rien acheté), puis recharge.
    async function reset(): Promise<void> {
      const r = await resetMe(tg?.initData ?? "");
      if (!r.ok) return void haptic("error");
      haptic("success");
      customItem = null;
      await refreshEntitlements(tg?.initData ?? "");
      enter("hub");
    }

    function choose(): void {
      window.removeEventListener("keydown", onKey);
      gb.remove();
      resolve({ theme: chosenTheme ?? themes[0]!, lang, mode, booster: chosenBooster ?? undefined });
    }

    // Lance la combinaison du jour : essai gratuit (même mode/style non possédés),
    // sans persister le mode ni booster (lancement instantané).
    function playFeatured(): void {
      if (!featured) return;
      const t = themes.find((x) => x.key === featured!.theme);
      const m = MODES.find((x) => x.key === featured!.mode);
      if (!t || !m) return;
      chosenTheme = t;
      mode = m;
      chosenBooster = null;
      choose();
    }
    function activate(): void {
      sfx.select();
      if (view === "hub") {
        if (sel === 0) playFeatured();
        else if (sel === 1) enter("style");
        else if (sel === 2) enter("daily");
        else if (sel === 3) shareLink(referralUrl(getUserId()), UI[lang].inviteText); // parrainage
        else if (sel === 4) enter("shop");
        else if (sel === 5) enter("board");
        else if (sel === 6) enter("profile");
        else if (sel === 7) openPerso();
        else if (sel === 8) enter("lang");
        else {
          setSoundEnabled(!isSoundEnabled());
          paint();
          if (isSoundEnabled()) sfx.nav(); // confirme que le son remarche
        }
      } else if (view === "style") {
        const t = themes[sel]!;
        const go = () => {
          chosenTheme = t;
          enter("modepick");
        };
        if (themeLocked(t)) void purchaseAndThen(THEME_ITEM[t.key]!, go); // achat sur place → choix du mode
        else go();
      } else if (view === "modepick") {
        if (sel >= MODES.length) {
          cycleBooster(); // ligne 🎒 BOOSTER : fait tourner le choix
          return;
        }
        const m = MODES[sel]!;
        const play = () => {
          mode = m;
          saveMode(m);
          choose();
        };
        if (!modeOwned(m)) void purchaseAndThen(`mode_${m.key}`, play); // achat sur place → lance la partie
        else play();
      } else if (view === "lang") {
        lang = LANGS[sel]!;
        saveLang(lang);
        enter("hub");
      } else if (view === "shop") {
        if (sel < shopItems.length) {
          const it = shopItems[sel];
          if (it) void acquire(it, () => view === "shop" && renderShop());
        } else {
          const b = catalogBoosters[sel - shopItems.length];
          if (b) void buyBoosterItem(b);
        }
      } else if (view === "perso") {
        if (ownsItem("custom_names")) {
          (rows[sel]?.querySelector("input") as HTMLInputElement | null)?.focus();
        } else if (customItem) {
          void acquire(customItem, () => view === "perso" && paint()); // déblocage sur place → champs
        }
      } else if (view === "dev") {
        void reset();
      } else if (view === "board") {
        // A cycle la période du classement : tout → jour → semaine → tout.
        boardPeriod = boardPeriod === "all" ? "day" : boardPeriod === "day" ? "week" : "all";
        renderData("board");
      } else if (view === "daily") {
        const o = dailyObjs[sel];
        if (o) void claimObjective(o);
      } else back(); // profile : A = retour
    }
    function onRowClick(): void {
      applySel();
      // Les champs de saisie (perso possédé) gèrent leur focus eux-mêmes (addInputRow,
      // pas addRow) ; toute ligne passée ici est donc bien activable.
      activate();
    }

    function onKey(e: KeyboardEvent): void {
      // Champ de saisie actif : on laisse le navigateur gérer la frappe.
      if (document.activeElement instanceof HTMLInputElement) {
        if (e.code === "Escape" || e.code === "Enter") {
          e.preventDefault();
          document.activeElement.blur();
        }
        return;
      }
      if (e.code === "ArrowUp") (e.preventDefault(), move(-1));
      else if (e.code === "ArrowDown") (e.preventDefault(), move(1));
      else if (e.code === "KeyS") (e.preventDefault(), openDev());
      else if (e.code === "Escape" || e.code === "Backspace") {
        if (view !== "hub") (e.preventDefault(), back());
      } else if (["Enter", "Space", "KeyA"].includes(e.code)) (e.preventDefault(), activate());
    }

    gb.querySelector(".up")!.addEventListener("click", () => move(-1));
    gb.querySelector(".down")!.addEventListener("click", () => move(1));
    gb.querySelector(".btnA")!.addEventListener("click", activate);
    gb.querySelector(".btnB")!.addEventListener("click", back);
    gb.querySelector(".btnSelect")!.addEventListener("click", openDev);
    window.addEventListener("keydown", onKey);

    enter("hub");
    // Précharge le catalogue (prix) et la combinaison du jour (ligne ⭐ du hub).
    void ensureCatalog().then(() => {
      if (view === "style" || view === "modepick") paint();
    });
    void ensureFeatured().then(() => {
      if (view === "hub") paint();
    });
    // Précharge les défis → badge 🎁 sur DÉFIS si une récompense attend.
    if (tg?.initData) {
      void getDaily(tg.initData).then((res) => {
        if (!res.ok) return;
        claimableCount = res.data.objectives.filter((o) => o.done && !o.claimed).length;
        if (view === "hub") paint();
      });
    }
    root.replaceChildren(gb);
  });
}
